import { format } from 'date-fns';

import {
    Building2Icon,
    BriefcaseIcon,
    CalendarDaysIcon,
    CalendarXIcon,
    ChartColumnIcon,
    ChartGantt,
    ClockAlertIcon,
    ClockIcon,
    FileXIcon,
    GavelIcon,
    HandshakeIcon,
    HistoryIcon,
    HourglassIcon,
    InboxIcon,
    ListChecksIcon,
    PhoneCallIcon,
    ReceiptIcon,
    ReceiptTextIcon,
    RefreshCwIcon,
    ScaleIcon,
    SearchIcon,
    SvgIconComponent,
    TagIcon,
    TrophyIcon,
    UserSearchIcon,
    UsersIcon,
    UserXIcon,
    WalletIcon,
} from 'components/icons';
import type { FamilyId } from 'utils/families';

export interface PromptOption {
    label: string;
    value: string;
}

export type PlaceholderInputType = 'contact' | 'matter' | 'group';

/** Chips shown next to a prompt on both surfaces; see `PromptBadges`. */
export type PromptBadge = 'agentic' | 'new';

/**
 * Whether a prompt still carries the New badge: until its `newUntil` day (`YYYY-MM-DD`), exclusive,
 * by the viewer's local calendar. A prompt with no date is never new.
 */
export const isNew = (newUntil: string | undefined, today: Date = new Date()): boolean =>
    newUntil != null && format(today, 'yyyy-MM-dd') < newUntil;

export type SamplePromptGroup = 'cases' | 'clients-bd' | 'invoicing' | 'my-work';

/** The gallery's group tabs on the AI home, in order, after Suggested. */
export const SAMPLE_PROMPT_GROUPS: Array<{ id: SamplePromptGroup; titleId: string; title: string }> = [
    { id: 'cases', titleId: 'ai-chat.sample-prompts.groups.cases', title: 'Cases' },
    { id: 'clients-bd', titleId: 'ai-chat.sample-prompts.groups.clients-bd', title: 'Clients & BD' },
    { id: 'invoicing', titleId: 'ai-chat.sample-prompts.groups.invoicing', title: 'Invoicing' },
    { id: 'my-work', titleId: 'ai-chat.sample-prompts.groups.my-work', title: 'My work' },
];

export interface SamplePrompt {
    id: string;
    group: SamplePromptGroup;
    /** Badges set by hand. New is not one: `getSamplePrompts` adds it from `newUntil`. */
    badges?: PromptBadge[];
    /** Day the New badge drops (`YYYY-MM-DD`), see `isNew`. */
    newUntil?: string;
    /** Message id for the card label, `ai-chat.sample-prompts.<id>.title`. */
    titleId: string;
    /** English copy behind `titleId`, passed as `defaultMessage`. */
    title: string;
    /** Message id for the card subtitle, `ai-chat.sample-prompts.<id>.preview`. */
    previewTextId: string;
    /** English copy behind `previewTextId`, passed as `defaultMessage`. */
    previewText: string;
    icon: SvgIconComponent;
    /** The family of the record the prompt is about, for its tile; none draws the neutral tile. */
    family?: FamilyId;
    prompt: string;
    instructions?: string;
    options?: PromptOption[];
    /**
     * Which placeholder `options` fill. Defaults to the first placeholder in the prompt, which is
     * only wrong when an earlier placeholder has its own picker — see `payment-behavior`.
     */
    optionsPlaceholder?: string;
    placeholderDefaults?: Record<string, string>;
    placeholderInputTypes?: Record<string, PlaceholderInputType>;
    placeholderNoteId?: string;
    /** Permission codes the prompt needs; shown only to a user holding at least one. Absent: shown to everyone. */
    requiredPermissions?: string[];
}

type DemoPromptOverride = Partial<
    Pick<
        SamplePrompt,
        | 'titleId'
        | 'title'
        | 'previewTextId'
        | 'previewText'
        | 'prompt'
        | 'instructions'
        | 'options'
        | 'optionsPlaceholder'
        | 'placeholderDefaults'
        | 'placeholderInputTypes'
        | 'placeholderNoteId'
    >
>;

// Values read naturally when substituted into "... [time period]" e.g. "from last week".
// Also offered by the floating assistant's page prompts (see floating/pagePrompts.ts).
export const TIME_PERIOD_OPTIONS_FULL: PromptOption[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This week', value: 'this week' },
    { label: 'Last week', value: 'last week' },
    { label: 'This month', value: 'this month' },
    { label: 'Last month', value: 'last month' },
    { label: 'This year', value: 'this year' },
    { label: 'Last year', value: 'last year' },
];

// For prompts where the finest meaningful grain is a month (analytics, pending offers).
export const TIME_PERIOD_OPTIONS_MONTH: PromptOption[] = TIME_PERIOD_OPTIONS_FULL.filter((option) =>
    ['this month', 'last month', 'this year', 'last year'].includes(option.value)
);

// Rolling windows, back for "no activity since" questions ("...in the past [time period]?") and forward
// for expiry ones ("...in the next [time period]?"). Prompts take the subset that makes sense for them
// via `lookbackSubset`.
export const LOOKBACK_OPTIONS: PromptOption[] = [
    { label: '1 month', value: '1 month' },
    { label: '3 months', value: '3 months' },
    { label: '6 months', value: '6 months' },
    { label: '12 months', value: '12 months' },
];

// Payment behaviour needs several years of invoices to average over, which the standard
// period list (single years at most) does not offer.
export const MULTI_YEAR_OPTIONS: PromptOption[] = [
    { label: 'Last year', value: 'last year' },
    { label: 'Last 2 years', value: 'last 2 years' },
    { label: 'Last 3 years', value: 'last 3 years' },
];

// The reminder steps after which a lawyer steps in on a debt.
export const REMINDER_STEP_OPTIONS: PromptOption[] = [
    { label: '3rd reminder', value: '3rd reminder' },
    { label: '4th reminder', value: '4th reminder' },
];

// Ageing buckets for "overdue by more than [days]".
export const OVERDUE_DAYS_OPTIONS: PromptOption[] = [
    { label: '30 days', value: '30 days' },
    { label: '60 days', value: '60 days' },
    { label: '90 days', value: '90 days' },
];

const lookbackSubset = (...values: string[]): PromptOption[] =>
    LOOKBACK_OPTIONS.filter((option) => values.includes(option.value));

// The standard "no activity in the past …" set. Also offered by the floating assistant's page prompts.
export const LOOKBACK_OPTIONS_STANDARD: PromptOption[] = lookbackSubset('3 months', '6 months', '12 months');

// Display order: SAMPLE_PROMPT_GROUPS groups, most valuable prompts first within each. Suggested shows each group's
// first three, so the order decides it.
const BASE_SAMPLE_PROMPTS: SamplePrompt[] = [
    {
        id: 'experience-record-search',
        group: 'cases',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.experience-record-search.title',
        title: 'Experience Search',
        previewTextId: 'ai-chat.sample-prompts.experience-record-search.preview',
        previewText: 'Find experience records about a topic',
        icon: SearchIcon,
        family: 'bd',
        prompt: 'Find experience records about [topic]',
    },
    {
        id: 'case-time-analysis',
        group: 'cases',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.case-time-analysis.title',
        title: 'Case Pricing Benchmark',
        previewTextId: 'ai-chat.sample-prompts.case-time-analysis.preview',
        previewText: 'Hours and cost by phase and seniority, for pricing',
        icon: ChartColumnIcon,
        prompt: 'Analyse the timecards of case [case number or name] as a pricing benchmark',
        placeholderInputTypes: { 'case number or name': 'matter' },
    },
    {
        id: 'case-status-update',
        group: 'cases',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.case-status-update.title',
        title: 'Case Status Update',
        previewTextId: 'ai-chat.sample-prompts.case-status-update.preview',
        previewText: 'Draft a client status update for a case',
        icon: RefreshCwIcon,
        family: 'cases',
        prompt: 'Draft a status update for the client of case [case number or name]',
        placeholderInputTypes: { 'case number or name': 'matter' },
    },
    {
        id: 'top-wip-cases',
        group: 'cases',
        titleId: 'ai-chat.sample-prompts.top-wip-cases.title',
        title: 'Top WIP Cases',
        previewTextId: 'ai-chat.sample-prompts.top-wip-cases.preview',
        previewText: 'Top 10 cases by uninvoiced amount',
        icon: BriefcaseIcon,
        family: 'cases',
        // No time period: WIP is a current balance, not something accumulated over a window.
        prompt: 'Show the top 10 cases by uninvoiced amount (WIP)',
    },
    {
        id: 'stale-cases',
        group: 'cases',
        titleId: 'ai-chat.sample-prompts.stale-cases.title',
        title: 'Stale Cases',
        previewTextId: 'ai-chat.sample-prompts.stale-cases.preview',
        previewText: 'Open cases with no timecards in 3 months',
        icon: HourglassIcon,
        family: 'cases',
        prompt: 'Which open cases have had no timecards in the last [time period]?',
        options: lookbackSubset('1 month', '3 months', '6 months'),
        placeholderDefaults: { 'time period': '3 months' },
    },
    {
        id: 'pending-offers',
        group: 'cases',
        titleId: 'ai-chat.sample-prompts.pending-offers.title',
        title: 'Pending Offers',
        previewTextId: 'ai-chat.sample-prompts.pending-offers.preview',
        previewText: 'Cases with pending offers this year',
        icon: ScaleIcon,
        family: 'cases',
        prompt: 'Show me cases with pending offers opened [time period]',
        options: TIME_PERIOD_OPTIONS_MONTH,
        placeholderDefaults: { 'time period': 'this year' },
    },
    {
        id: 'case-intake',
        group: 'cases',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.case-intake.title',
        title: 'Case Intake',
        previewTextId: 'ai-chat.sample-prompts.case-intake.preview',
        previewText: 'Cases opened and closed per month, with chart',
        icon: InboxIcon,
        family: 'cases',
        prompt: 'How many cases did we open and close [time period], by month? Show a chart.',
        options: TIME_PERIOD_OPTIONS_MONTH,
        placeholderDefaults: { 'time period': 'this year' },
    },
    {
        id: 'case-search',
        group: 'cases',
        titleId: 'ai-chat.sample-prompts.case-search.title',
        title: 'Case Search',
        previewTextId: 'ai-chat.sample-prompts.case-search.preview',
        previewText: 'Find cases about a topic',
        icon: SearchIcon,
        family: 'cases',
        prompt: 'Find cases about [topic]',
    },
    {
        id: 'whom-to-call-next',
        group: 'clients-bd',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.whom-to-call-next.title',
        title: 'Whom to Call Next?',
        previewTextId: 'ai-chat.sample-prompts.whom-to-call-next.preview',
        previewText: 'Portfolio clients most overdue for a meeting',
        icon: PhoneCallIcon,
        family: 'contacts',
        prompt: 'Whom should I call next? Suggest 5 clients from my portfolio who are most overdue for a meeting.',
    },
    {
        id: 'meeting-prep',
        group: 'clients-bd',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.meeting-prep.title',
        title: 'Meeting Prep',
        previewTextId: 'ai-chat.sample-prompts.meeting-prep.preview',
        previewText: 'Prepare a meeting brief for a contact',
        icon: CalendarDaysIcon,
        family: 'bd',
        prompt: 'Prepare a meeting brief for [contact name]',
        placeholderInputTypes: { 'contact name': 'contact' },
    },
    {
        id: 'group-meeting-prep',
        group: 'clients-bd',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.group-meeting-prep.title',
        title: 'Group Meeting Prep',
        previewTextId: 'ai-chat.sample-prompts.group-meeting-prep.preview',
        previewText: 'Prepare a meeting brief for a group',
        icon: UsersIcon,
        family: 'bd',
        prompt: 'Prepare a meeting brief for the group [group name]',
        placeholderInputTypes: { 'group name': 'group' },
        requiredPermissions: ['READ.PARTY_GROUPS.ALL', 'READ.PARTY_GROUPS.MY_OWN'],
    },
    {
        id: 'contact-360',
        group: 'clients-bd',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.contact-360.title',
        title: 'Contact 360°',
        previewTextId: 'ai-chat.sample-prompts.contact-360.preview',
        previewText: 'A full 360° view of one contact',
        icon: UserSearchIcon,
        family: 'contacts',
        prompt: 'Give me a 360° view of [contact name]',
        placeholderInputTypes: { 'contact name': 'contact' },
    },
    {
        id: 'client-relationship-summary',
        group: 'clients-bd',
        badges: ['agentic'],
        newUntil: '2026-11-06',
        titleId: 'ai-chat.sample-prompts.client-relationship-summary.title',
        title: 'Client Relationship Summary',
        previewTextId: 'ai-chat.sample-prompts.client-relationship-summary.preview',
        previewText: 'How the relationship developed and what we know',
        icon: ChartGantt,
        family: 'contacts',
        prompt: 'Summarise our relationship with client [client name]',
        placeholderInputTypes: { 'client name': 'contact' },
    },
    {
        id: 'payment-behavior',
        group: 'clients-bd',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.payment-behavior.title',
        title: 'Payment Behaviour',
        previewTextId: 'ai-chat.sample-prompts.payment-behavior.preview',
        previewText: 'How fast a client pays, and what’s overdue',
        icon: WalletIcon,
        family: 'finance',
        prompt: 'Analyse payment behaviour of [client name] over [time period]: average days from invoice due date to payment, and overdue invoices',
        options: MULTI_YEAR_OPTIONS,
        // [client name] comes first and has its own contact picker, so the period options have to
        // name their placeholder explicitly.
        optionsPlaceholder: 'time period',
        placeholderDefaults: { 'time period': 'last 2 years' },
        placeholderInputTypes: { 'client name': 'contact' },
    },
    {
        id: 'top-clients',
        group: 'clients-bd',
        titleId: 'ai-chat.sample-prompts.top-clients.title',
        title: 'Top Clients',
        previewTextId: 'ai-chat.sample-prompts.top-clients.preview',
        previewText: 'Top clients by billable hours last year',
        icon: TrophyIcon,
        family: 'contacts',
        prompt: 'Who are our top clients by billable hours [time period]?',
        options: TIME_PERIOD_OPTIONS_MONTH,
        placeholderDefaults: { 'time period': 'last year' },
    },
    {
        id: 'top-clients-by-cases',
        group: 'clients-bd',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.top-clients-by-cases.title',
        title: 'Top Clients by Cases',
        previewTextId: 'ai-chat.sample-prompts.top-clients-by-cases.preview',
        previewText: 'Clients with the most new cases this year',
        icon: Building2Icon,
        family: 'contacts',
        prompt: 'Which clients had the most cases opened [time period]?',
        options: TIME_PERIOD_OPTIONS_MONTH,
        placeholderDefaults: { 'time period': 'this year' },
    },
    {
        id: 'bd-follow-ups',
        group: 'clients-bd',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.bd-follow-ups.title',
        title: 'BD Follow-ups',
        previewTextId: 'ai-chat.sample-prompts.bd-follow-ups.preview',
        previewText: 'Follow-up actions for last month’s BD efforts',
        icon: HandshakeIcon,
        family: 'bd',
        prompt: 'Review my BD efforts from [time period] and suggest a follow-up action for each',
        options: TIME_PERIOD_OPTIONS_FULL,
        placeholderDefaults: { 'time period': 'last month' },
    },
    {
        id: 'bd-tasks-due',
        group: 'clients-bd',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.bd-tasks-due.title',
        title: 'BD Follow-up Tasks',
        previewTextId: 'ai-chat.sample-prompts.bd-tasks-due.preview',
        previewText: 'BD follow-up tasks due in the next month',
        icon: ListChecksIcon,
        family: 'bd',
        // The tasks recorded as BD follow-ups, not the efforts the card above reviews.
        prompt: 'What BD follow-ups do I have due in the next [time period]?',
        options: lookbackSubset('1 month', '3 months', '6 months'),
        placeholderDefaults: { 'time period': '1 month' },
    },
    {
        id: 'dormant-clients',
        group: 'clients-bd',
        titleId: 'ai-chat.sample-prompts.dormant-clients.title',
        title: 'Dormant Clients',
        previewTextId: 'ai-chat.sample-prompts.dormant-clients.preview',
        previewText: 'Clients with no BD activity in 6 months',
        icon: UserXIcon,
        family: 'contacts',
        // Clients and prospects rather than all contacts — opposing parties and the like are not BD
        // targets, so their silence means nothing.
        prompt: 'Which clients and prospects have had no BD activity in the past [time period]?',
        options: LOOKBACK_OPTIONS_STANDARD,
        placeholderDefaults: { 'time period': '6 months' },
    },
    {
        id: 'client-rates',
        group: 'clients-bd',
        titleId: 'ai-chat.sample-prompts.client-rates.title',
        title: 'Client Rates',
        previewTextId: 'ai-chat.sample-prompts.client-rates.preview',
        previewText: 'Hourly rates that apply to a client',
        icon: TagIcon,
        family: 'finance',
        prompt: 'What hourly rates apply to client [client name]?',
        placeholderInputTypes: { 'client name': 'contact' },
    },
    {
        id: 'invoice-narrative-review',
        group: 'invoicing',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.invoice-narrative-review.title',
        title: 'Invoice Narrative Review',
        previewTextId: 'ai-chat.sample-prompts.invoice-narrative-review.preview',
        previewText: 'Review time entries and invoice narrative',
        icon: ReceiptTextIcon,
        family: 'finance',
        prompt: 'Look at case [case number or name] and recorded timecards, and review the recorded time entries and the overall invoice narrative.',
        instructions:
            'Identify deficiencies at both the individual entry level and the aggregate level, and suggest improvements to ensure the work performed is clearly described, coherent as a whole, and sufficiently detailed to justify the time billed.',
        placeholderInputTypes: { 'case number or name': 'matter' },
    },
    {
        id: 'overdue-invoices',
        group: 'invoicing',
        titleId: 'ai-chat.sample-prompts.overdue-invoices.title',
        title: 'Overdue Invoices',
        previewTextId: 'ai-chat.sample-prompts.overdue-invoices.preview',
        previewText: 'Invoices overdue by more than 60 days',
        icon: ClockAlertIcon,
        family: 'finance',
        prompt: 'List invoices currently overdue by more than [days], grouped by client',
        options: OVERDUE_DAYS_OPTIONS,
        placeholderDefaults: { days: '60 days' },
    },
    {
        id: 'debts-needing-my-help',
        group: 'invoicing',
        newUntil: '2026-11-20',
        titleId: 'ai-chat.sample-prompts.debts-needing-my-help.title',
        title: 'Debts Needing My Help',
        previewTextId: 'ai-chat.sample-prompts.debts-needing-my-help.preview',
        previewText: 'Unpaid invoices after the 3rd reminder on my cases and clients',
        icon: GavelIcon,
        family: 'finance',
        prompt: 'List unpaid overdue invoices after the [reminder] on my cases and clients, grouped by client, with the highest reminder sent and its date',
        options: REMINDER_STEP_OPTIONS,
        placeholderDefaults: { reminder: '3rd reminder' },
    },
    {
        id: 'uninvoiced-clients',
        group: 'invoicing',
        titleId: 'ai-chat.sample-prompts.uninvoiced-clients.title',
        title: 'Uninvoiced Clients',
        previewTextId: 'ai-chat.sample-prompts.uninvoiced-clients.preview',
        previewText: 'Clients not invoiced at all this year',
        icon: FileXIcon,
        family: 'finance',
        prompt: 'Which clients have not been invoiced at all [time period]?',
        options: TIME_PERIOD_OPTIONS_FULL,
        placeholderDefaults: { 'time period': 'this year' },
    },
    {
        id: 'admin-fees',
        group: 'invoicing',
        newUntil: '2026-10-30',
        titleId: 'ai-chat.sample-prompts.admin-fees.title',
        title: 'Admin Fees',
        previewTextId: 'ai-chat.sample-prompts.admin-fees.preview',
        previewText: 'Admin fees charged to a client this year',
        icon: ReceiptIcon,
        family: 'finance',
        prompt: 'Show admin fees charged to [client name] [time period]',
        options: TIME_PERIOD_OPTIONS_MONTH,
        // [client name] comes first and has its own contact picker, so the period options have to
        // name their placeholder explicitly.
        optionsPlaceholder: 'time period',
        placeholderDefaults: { 'time period': 'this year' },
        placeholderInputTypes: { 'client name': 'contact' },
    },
    {
        id: 'timecard-quality',
        group: 'my-work',
        badges: ['agentic'],
        titleId: 'ai-chat.sample-prompts.timecard-quality.title',
        title: 'Timecard Quality',
        previewTextId: 'ai-chat.sample-prompts.timecard-quality.preview',
        previewText: 'Improve last month’s timecard descriptions',
        icon: ClockIcon,
        prompt: 'Review my timecards from [time period] and suggest how to improve their descriptions',
        options: TIME_PERIOD_OPTIONS_FULL,
        placeholderDefaults: { 'time period': 'last month' },
    },
    {
        id: 'missed-time',
        group: 'my-work',
        titleId: 'ai-chat.sample-prompts.missed-time.title',
        title: 'Missed Time',
        previewTextId: 'ai-chat.sample-prompts.missed-time.preview',
        previewText: 'Days with no recorded time this week',
        icon: CalendarXIcon,
        prompt: 'Did I miss recording time on any day [time period]?',
        options: TIME_PERIOD_OPTIONS_FULL,
        placeholderDefaults: { 'time period': 'this week' },
    },
    {
        id: 'billable-hours-chart',
        group: 'my-work',
        titleId: 'ai-chat.sample-prompts.billable-hours-chart.title',
        title: 'Billable Hours Chart',
        previewTextId: 'ai-chat.sample-prompts.billable-hours-chart.preview',
        previewText: 'Billable hours last year with chart',
        icon: ChartColumnIcon,
        family: 'reports',
        prompt: 'Summarise my billable hours [time period] with the most appropriate breakdown (days, weeks, or months) and a chart.',
        options: TIME_PERIOD_OPTIONS_FULL,
        placeholderDefaults: { 'time period': 'last year' },
    },
    {
        id: 'timecard-search',
        group: 'my-work',
        titleId: 'ai-chat.sample-prompts.timecard-search.title',
        title: 'Timecard Search',
        previewTextId: 'ai-chat.sample-prompts.timecard-search.preview',
        previewText: 'My timecards mentioning a keyword this year',
        icon: HistoryIcon,
        prompt: 'Find my timecards mentioning [keyword] from [time period]',
        options: TIME_PERIOD_OPTIONS_FULL,
        // [keyword] comes first and is typed, so the period options have to name their placeholder.
        optionsPlaceholder: 'time period',
        placeholderDefaults: { 'time period': 'this year' },
    },
];

// Pilot tenants (FeatureFlag.AI_CHAT_DEMO_PLACEHOLDER_DEFAULTS) run on sparse, dated dummy data and
// their users have no personal timecards or BD efforts. Widen the periods and add a fallback
// instruction so a demo prompt still returns something worth looking at.
const DEMO_OVERRIDES: Partial<Record<string, DemoPromptOverride>> = {
    'missed-time': {
        instructions:
            "If I have no timecards of my own in this period, analyse the firm's timecards instead and show which employees have days without recorded time.",
    },
    'timecard-quality': {
        previewTextId: 'ai-chat.sample-prompts.timecard-quality.preview-demo',
        previewText: 'Improve timecard descriptions from this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions: 'If I have no timecards of my own, review the 10 most recent timecards in the firm instead.',
    },
    'billable-hours-chart': {
        previewTextId: 'ai-chat.sample-prompts.billable-hours-chart.preview-demo',
        previewText: 'Billable hours this and last year with chart',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions:
            "If I have no billable hours of my own in this period, summarise our firm's billable hours instead.",
    },
    'bd-follow-ups': {
        previewTextId: 'ai-chat.sample-prompts.bd-follow-ups.preview-demo',
        previewText: 'Follow-up actions for BD efforts from this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions:
            "If I have no BD efforts of my own, use the firm's most recent BD efforts. Limit to the 10 most recent.",
    },
    'timecard-search': {
        previewTextId: 'ai-chat.sample-prompts.timecard-search.preview-demo',
        previewText: 'Timecards mentioning a keyword from this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions: "If I have no timecards of my own, search the firm's timecards instead.",
    },
    'dormant-clients': {
        previewTextId: 'ai-chat.sample-prompts.dormant-clients.preview-demo',
        previewText: 'Clients with no BD activity in 12 months',
        placeholderDefaults: { 'time period': '12 months' },
        instructions: 'If most clients match, show only the 10 clients with the most BD activity before it stopped.',
    },
    'top-clients': {
        previewTextId: 'ai-chat.sample-prompts.top-clients.preview-demo',
        previewText: 'Top clients by billable hours this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
    },
    'stale-cases': {
        instructions: 'If most open cases match, show only the 10 most recently active ones.',
    },
    'pending-offers': {
        previewTextId: 'ai-chat.sample-prompts.pending-offers.preview-demo',
        previewText: 'Cases with pending offers from this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions: 'If none are found, show the most recent active cases from the same period.',
    },
    'overdue-invoices': {
        instructions: 'Limit to the top 20 by outstanding amount.',
    },
    'uninvoiced-clients': {
        previewTextId: 'ai-chat.sample-prompts.uninvoiced-clients.preview-demo',
        previewText: 'Clients not invoiced at all in this and last year',
        placeholderDefaults: { 'time period': 'this and last year' },
        instructions: 'If most clients match, show only the 10 with the most invoiced revenue historically.',
    },
};

export const getSamplePrompts = (demoOverridesEnabled = false, today: Date = new Date()): SamplePrompt[] => {
    const overrides = demoOverridesEnabled ? DEMO_OVERRIDES : {};

    return BASE_SAMPLE_PROMPTS.map((base) => {
        const prompt: SamplePrompt = isNew(base.newUntil, today)
            ? { ...base, badges: [...(base.badges ?? []), 'new'] }
            : base;
        const override = overrides[prompt.id];
        if (!override) return prompt;
        const merged: SamplePrompt = { ...prompt, ...override };
        if (prompt.placeholderDefaults && override.placeholderDefaults) {
            merged.placeholderDefaults = { ...prompt.placeholderDefaults, ...override.placeholderDefaults };
        }
        return merged;
    });
};

/** Can a user holding these permission codes run the prompt: it needs none, or one of its own. */
const canRun = (prompt: SamplePrompt, permissionCodes: string[]): boolean =>
    !prompt.requiredPermissions || prompt.requiredPermissions.some((code) => permissionCodes.includes(code));

/** The prompts a user holding these permission codes may run, in catalogue order. */
export const getRunnablePrompts = (prompts: SamplePrompt[], permissionCodes: string[]): SamplePrompt[] =>
    prompts.filter((prompt) => canRun(prompt, permissionCodes));

/**
 * The AI home's Suggested tab: the first three prompts of each group, group by group in `SAMPLE_PROMPT_GROUPS`
 * order. Pass the prompts the user may run, so a prompt they cannot see leaves its place to the next one.
 */
export const getSuggestedPrompts = (prompts: SamplePrompt[]): SamplePrompt[] =>
    SAMPLE_PROMPT_GROUPS.flatMap((group) => prompts.filter((prompt) => prompt.group === group.id).slice(0, 3));

/** How many of the Suggested prompts the phone's home lists. */
export const PHONE_PROMPT_COUNT = 6;
